import { z } from 'zod';
import { ChatModelSchema } from './chat.types.js';
import { ModelProviderSecretIdSchema } from './model-provider-secrets.types.js';

/**
 * Org-scoped registry of "LLM tasks": discrete jobs the platform performs
 * by hitting a model provider (the auto-titler that the agent launcher
 * uses, and the Slack interaction classifier). Each task has a
 * label/description shown in admin UIs; an optional org assignment
 * overrides the default model + key choice, or disables the task entirely.
 *
 * Absence of a row for a task means "use the platform default" (currently
 * `haiku` resolved against the sole org-tier Anthropic key, falling back
 * to the non-LLM path when no key exists). An assignment may instead pick
 * any chat model; the task dispatches to that model's provider, so available
 * model providers work as long as the org has a key for them. A row with
 * `enabled=false` unconditionally short-circuits to the non-LLM fallback.
 */
export const LlmTaskIdSchema = z.enum(['agent-summary', 'slack-interaction', 'change-summary']);
export type LlmTaskId = z.infer<typeof LlmTaskIdSchema>;

export interface LlmTaskDefinition {
  id: LlmTaskId;
  label: string;
  description: string;
}

export const LLM_TASK_DEFINITIONS: readonly LlmTaskDefinition[] = [
  {
    id: 'agent-summary',
    label: 'Automatic Agent summary',
    description: 'Generates a concise title/summary for each agent run from its prompt.',
  },
  {
    id: 'slack-interaction',
    label: 'Slack interaction',
    description:
      'Interprets messages sent to Reflex in Slack: analyzes the task and resolves the repository and persona before starting an agent.',
  },
  {
    id: 'change-summary',
    label: 'Change summary',
    description:
      'Titles a change from its diff when a new version is published, such as the version note on a published flow. The title stays editable afterwards.',
  },
] as const;

export const LlmTaskAssignmentIdSchema = z
  .string()
  .regex(/^lta_[A-Za-z0-9]{22}$/, 'Expected id like lta_XXXXXXXXXXXXXXXXXXXXXX');

export const LlmTaskAssignmentSchema = z
  .object({
    id: LlmTaskAssignmentIdSchema,
    organizationId: z.string(),
    taskId: LlmTaskIdSchema,
    enabled: z.boolean(),
    /** Null when the row is disabled or no override has been chosen yet. */
    model: ChatModelSchema.nullable(),
    /** `mps_*` id of the key the task should use; null = fall back to the sole org-tier key. */
    providerSecretId: ModelProviderSecretIdSchema.nullable(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .meta({ id: 'LlmTaskAssignment' });
export type LlmTaskAssignment = z.infer<typeof LlmTaskAssignmentSchema>;

export const LlmTaskAssignmentsListResponseSchema = z.object({
  assignments: z.array(LlmTaskAssignmentSchema),
});
export type LlmTaskAssignmentsListResponse = z.infer<typeof LlmTaskAssignmentsListResponseSchema>;

export const UpsertLlmTaskAssignmentRequestSchema = z.object({
  enabled: z.boolean(),
  model: ChatModelSchema.nullable().optional(),
  providerSecretId: ModelProviderSecretIdSchema.nullable().optional(),
});
export type UpsertLlmTaskAssignmentRequest = z.infer<typeof UpsertLlmTaskAssignmentRequestSchema>;
